import { Box } from "@mui/material";
import { PortableText, type PortableTextComponents } from "@portabletext/react";

import { codePreStyle } from "@/content/codePreStyle";
import type { ArticleBody } from "@/content/types";
import { marketingHref, newTabProps } from "../marketingLinks";

const idOf = (value: unknown): string | undefined => {
  const id = (value as { _headingId?: unknown })?._headingId;
  return typeof id === "string" ? id : undefined;
};

/** A body picture as the adapter hands it over. */
type PictureValue = {
  url?: unknown;
  alt?: unknown;
  caption?: unknown;
  align?: unknown;
  maxWidth?: unknown;
};

/**
 * The figure's own attributes. The editor's width, which the article
 * stylesheet draws: a centred figure is 60% of the column, as the published
 * article prints it. And a cap the post's published figure holds its picture
 * to (`figureCaps.ts`), printed where Webflow printed it, on the figure, so the
 * picture's border is drawn inside its width.
 */
const figureOf = ({ align, maxWidth }: PictureValue) => ({
  "data-align": align ? String(align) : undefined,
  style:
    typeof maxWidth === "number" ? { maxWidth: `${maxWidth}px` } : undefined,
});

/**
 * A picture in Webflow's figure and wrapper. The wrapper is where the
 * picture's line box sits, so a short picture's figure is as tall as the
 * published one.
 */
function Picture({ value }: { value: PictureValue }) {
  return (
    <Box component="figure" {...figureOf(value)} sx={{ m: 0 }}>
      <div>
        <Box
          component="img"
          src={String(value.url ?? "")}
          alt={String(value.alt ?? "")}
          loading="lazy"
          // Its own size, capped at the column; the article stylesheet sizes
          // it to the wrapper, as Webflow does.
          sx={{ maxWidth: "100%", height: "auto" }}
        />
      </div>
      {value.caption ? (
        <Box component="figcaption">{String(value.caption)}</Box>
      ) : null}
    </Box>
  );
}

const BLOCK_COMPONENTS: PortableTextComponents = {
  types: {
    image: ({ value }) => <Picture value={value ?? {}} />,
    /** 16:9 by construction: the frame's stored size is the embed vendor's, and a fixed pair inside a fluid column letterboxes at every width but one. */
    videoEmbed: ({ value }) => (
      <Box
        sx={{
          position: "relative",
          width: 1,
          aspectRatio: "16 / 9",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <Box
          component="iframe"
          src={String(value?.url ?? "")}
          title={value?.title ? String(value.title) : "Video"}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{
            position: "absolute",
            inset: 0,
            width: 1,
            height: 1,
            border: 0,
          }}
        />
      </Box>
    ),
    // Painted by the content adapter on the server, as the published page
    // draws it, so the highlighters stay there and the browser is handed markup.
    codeBlock: ({ value }) => (
      <pre className="w-code-block" style={codePreStyle}>
        <code
          className={String(value?.className ?? "")}
          style={{ whiteSpace: "pre-wrap" }}
          dangerouslySetInnerHTML={{ __html: String(value?.highlighted ?? "") }}
        />
      </pre>
    ),
    // Ends the list above, so the next is a list of its own, as the published
    // article draws two lists written one after the other; it draws nothing.
    listBreak: () => null,
    // Webflow's Embed element, printed as the editor pasted it: every table
    // the published bodies show is one, styled by its own code. It is not
    // sanitized, as the Studio's custom code is not; the Studio's editors are
    // the site's Webflow Designers.
    htmlEmbed: ({ value }) => (
      <div
        className="w-embed"
        dangerouslySetInnerHTML={{ __html: String(value?.code ?? "") }}
      />
    ),
  },
  block: {
    // The adapter stamps the id, so the contents rail and the heading agree
    // without the slug rule existing in two places.
    h2: ({ value, children }) => <h2 id={idOf(value)}>{children}</h2>,
    h3: ({ value, children }) => <h3 id={idOf(value)}>{children}</h3>,
    h4: ({ value, children }) => <h4 id={idOf(value)}>{children}</h4>,
  },
  marks: {
    link: ({ value, children }) => (
      <a
        href={marketingHref(String(value?.href ?? ""))}
        {...newTabProps({ newTab: value?.newTab === true })}
      >
        {children}
      </a>
    ),
  },
};

/**
 * The article body, rendered from whichever form its CMS holds it in. A CMS
 * that stores structured text hands over blocks and the components above are
 * the design; Webflow stores an HTML string instead, so that branch injects
 * already-sanitized markup and leans on the `.blog-content` stylesheet. The
 * HTML branch exists only while Webflow does.
 */
export default function ArticleContent({ body }: { body: ArticleBody }) {
  if (body.kind === "html") {
    // Sanitized upstream in `src/content/sanitize.ts`, at the adapter boundary.
    return <div dangerouslySetInnerHTML={{ __html: body.html }} />;
  }
  return <PortableText value={body.blocks} components={BLOCK_COMPONENTS} />;
}
